import React, { useState, useEffect } from 'react';

import 'react-toggle/style.css';

import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

import Signin from './containers/Signin';
import Signup from './containers/Signup';
import FranchiseSignup from './containers/FranchiseSignup';
import Loads from './containers/Loads';

import Dashboard from './containers/Dashboard';
import Invoices from './containers/Invoices';
import ArchivedInvoices from './containers/Invoices/ArchivedInvoices';
import InvoicePDF from './containers/InvoicePDF/PreviewPdf';

import Converters from './containers/Converters';
import ConverterDetail from './containers/Converters/ConverterDetail';
import IdentifyConverters from './containers/Converters/IdentifyConverters';
import VehicleSearch from './containers/Converters/VehicleSearch';

import PriceModifiers from './containers/PriceModifiers';
import Pricing from './containers/Pricing';
import Roles from './containers/Roles';
import Permissions from './containers/Permissions';
import Entities from './containers/Entities';
import CreateEntity from './containers/Entities/Create';
import NewUserProfile from './containers/NewUser/Create';
import Groups from './containers/Groups';
import GroupDetails from './containers/Entities/GroupDetails';
import Users from './containers/Users';
import UserCredits from './containers/UserCredits';
import HedgeManagement from './containers/HedgeManagement';
import Hedge from './containers/Hedge';
import Facilities from './containers/Facilities';
import FacilitiesConfig from './containers/FacilitiesConfig';
import MakeAndModel from './containers/MakeAndModel';
import ExpertAdvice from './containers/ExpertAdvice';
import Settings from './containers/Settings';
import SalesHistory from './containers/SalesHistory';
import SettingsConfig from './containers/SettingsConfig';
import Logout from './containers/Logout';
import CreateLoads from './containers/Loads/CreateLoads';
import ActiveLoads from './containers/Loads/ActiveLoads';
import LoadsReport from './containers/Loads/LoadsReport';
import LoadsCart from './containers/Loads/LoadsCart';
import BulkUpload from './containers/BulkUpload';
import DownloadCustomers from './containers/DownloadCustomers';
import InviteUsers from './containers/InviteUsers';
import Logs from './containers/Logs';
import LogUserDetail from './containers/Logs/LogUserDetail';
import PreviewPricing from './containers/Pricing/PreviewPricing';
import LoadReport from './containers/Dashboard/LoadReport';
import TopCats from './containers/Dashboard/TopCats';
import TopCustomers from './containers/Dashboard/Customer';
import DidNotBuyReport from './containers/Dashboard/DidNotBuyReport';

import TopLookups from './containers/Dashboard/TopLookups';
import TransactionsReport from './containers/Dashboard/TransactionsReport';
import GlobalModifierReport from './containers/Dashboard/GlobalModifierReport';
import Insights from './containers/Insights';
import ConverterTesting from './containers/Insights/ConverterTesting';
import CustomHedge from './containers/Insights/CustomHedge';
import HedgeLock from './containers/Insights/HedgeLock';
import AssayInsights from './containers/Insights/AssayInsights';
import HedgePlanning from './containers/Insights/HedgePlanning';
import LotHedgeSettlement from './containers/Insights/LotHedgeSettlement';
import CxoSettlementDashboard from './containers/Insights/CxoSettlementDashboard';
import UnderpaymentLeakInsights from './containers/Insights/UnderpaymentLeakInsights';
import BuyerLocation from './containers/Insights/BuyerLocation';
import BuyingInsights from './containers/Insights/BuyingInsights';
import KycRedirect from './containers/Kyc';
import KycGate from './components/KycGate';
import KycPendingOverlay from './components/KycPendingOverlay';

import Settlement from './containers/Settlement';
import CreateSettlement from './containers/Settlement/CreateSettlement';

import Payment from './containers/Payment';
import ConfigPayment from './containers/Payment/ConfigPayment';
import QuickBooks from './containers/QuickBooks';

// Virtual Grading imports
import VirtualGrading from './containers/VirtualGrading';
import UploadItem from './containers/VirtualGrading/UploadItem';
import ItemDetail from './containers/VirtualGrading/ItemDetail';
import AdminDashboard from './containers/VirtualGrading/AdminDashboard';
import GradeItem from './containers/VirtualGrading/GradeItem';
import AdminItemsList from './containers/VirtualGrading/AdminItemsList';

// v2 components
// import LoadsCartV2 from "./containers/V2/Loads/LoadsCart";
// import ActiveLoadsV2 from "./containers/V2/Loads/ActiveLoads";
// import CreateLoadsV2 from "./containers/V2/Loads/CreateLoads";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

function App() {
  const [isMobile, setIsMobile] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (windowDimensions.width < 720) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowDimensions]);

  return (
    <Router>
      <KycGate />
      <KycPendingOverlay />
      <Switch>
        <Route exact path='/' component={Signin} />
        <Route exact path='/login/:username' component={Signin} />
        <Route exact path='/invite' component={Signin} />
        <Route exact path='/signup' component={Signup} />
        <Route exact path='/franchise-signup' component={FranchiseSignup} />
        <Route exact path='/logout' component={Logout} />
        <Route exact path='/kyc' component={KycRedirect} />

        <Route exact path='/config' component={BulkUpload} />
        <Route exact path='/config/bulk-upload' component={BulkUpload} />
        <Route
          exact
          path='/config/download-customers'
          component={DownloadCustomers}
        />
        <Route exact path='/config/cart-config' component={FacilitiesConfig} />
        <Route exact path='/config/logs/user/:userId' component={LogUserDetail} />
        <Route exact path='/config/logs' component={Logs} />

        <Route exact path='/loads' component={ActiveLoads} />
        <Route exact path='/invoices/load/:loadId' component={LoadsCart} />
        <Route
          exact
          path='/invoices/Franchise/load/:loadId'
          component={LoadsCart}
        />
        <Route
          exact
          path='/invoices/settled/load/:loadId'
          component={LoadsCart}
        />
        <Route
          exact
          path='/invoices/archived/load/:loadId'
          component={LoadsCart}
        />
        <Route exact path='/my-cart/load/:loadId' component={LoadsCart} />
        <Route exact path='/loads/my-cart' component={ActiveLoads} />
        <Route exact path='/loads/reports' component={LoadsReport} />
        <Route exact path='/dashboard' component={Dashboard} />
        <Route exact path='/dashboard/costsheetreport' component={Dashboard} />
        <Route
          exact
          path='/dashboard/transactionsreport'
          component={TransactionsReport}
        />
        <Route exact path='/dashboard/loadreport' component={LoadReport} />
        <Route exact path='/dashboard/top-cats' component={TopCats} />
        <Route exact path='/dashboard/top-customers' component={TopCustomers} />
        <Route exact path='/dashboard/didNotBuy' component={DidNotBuyReport} />
        <Route
          exact
          path='/dashboard/globalmodifier'
          component={GlobalModifierReport}
        />
        <Route exact path='/dashboard/top-lookups' component={TopLookups} />
        <Route exact path='/insights' component={Insights} />
        <Route exact path='/insights/testing' component={ConverterTesting} />
        <Route exact path='/insights/custom-hedge' component={CustomHedge} />
        <Route exact path='/insights/hedge-planning' component={HedgePlanning} />
        <Route exact path='/insights/lot-hedge' component={LotHedgeSettlement} />
        <Route exact path='/insights/cxo-settlement' component={CxoSettlementDashboard} />
        <Route exact path='/insights/underpayment-leak' component={UnderpaymentLeakInsights} />
        <Route exact path='/insights/location' component={BuyerLocation} />
        <Route exact path='/insights/buying' component={BuyingInsights} />
        <Route exact path='/insights/hedge-lock' component={HedgeLock} />
        <Route exact path='/insights/assay' component={AssayInsights} />

        <Route exact path='/invoices' component={Invoices} />
        <Route exact path='/invoices/pending' component={Invoices} />
        <Route exact path='/invoices/settled' component={Invoices} />
        <Route exact path='/invoices/archived' component={ArchivedInvoices} />
        <Route exact path='/invoices/franchise' component={Invoices} />
        <Route exact path='/invoices/myInvoices' component={Invoices} />
        <Route exact path='/invoices/pdf/:loadId' component={InvoicePDF} />

        <Route exact path='/converters' component={Converters} />
        <Route
          exact
          path='/converters/identify/:parameters?'
          component={IdentifyConverters}
        />
        <Route exact path='/converters/vehicle' component={VehicleSearch} />
        <Route
          exact
          path='/converter/:id/:type?/:parameters?'
          component={ConverterDetail}
        />
        <Route exact path='/pricing' component={PreviewPricing} />
        <Route exact path='/previewPricing' component={PreviewPricing} />
        <Route exact path='/converters/make-model' component={MakeAndModel} />
        <Route
          exact
          path='/converters/expert-advice'
          component={ExpertAdvice}
        />
        {/* <Route exact path="/converters/bulk-upload" component={BulkUpload} /> */}

        <Route exact path='/price-modifiers' component={PriceModifiers} />
        <Route exact path='/config/roles' component={Roles} />
        <Route exact path='/config/Permissions' component={Permissions} />
        <Route
          exact
          path='/entities/viewGroup/:searchText?'
          component={Groups}
        />
        <Route exact path='/entities' component={Groups} />
        <Route
          exact
          path='/entities/viewGroupId/:entityId/:searchText?'
          component={GroupDetails}
        />
        <Route exact path='/config/facilities' component={Facilities} />
        <Route exact path='/config/sageinvoice' component={Invoices} />
        <Route exact path='/config/quickbooks' component={QuickBooks} />
        <Route exact path='/entities/users' component={Users} />
        <Route exact path='/entities/Franchise' component={Groups} />
        <Route exact path='/entities/Franchise/:companyId' component={Groups} />
        <Route exact path='/entities/Franchise/users' component={Users} />
        <Route
          exact
          path='/entities/Franchise/viewGroupId/:entityId/:searchText?'
          component={GroupDetails}
        />
        <Route exact path='/entities/create' component={CreateEntity} />
        <Route exact path='/entities/inviteUsers' component={InviteUsers} />
        <Route exact path='/entities/credits/:companyId' component={UserCredits} />
        <Route exact path='/entities/hedge/:companyId' component={HedgeManagement} />
        <Route exact path='/new-user/:id?' component={NewUserProfile} />

        <Route exact path='/settings' component={Settings} />
        <Route exact path='/settings-config' component={SettingsConfig} />
        <Route exact path='/sales' component={SalesHistory} />
        <Route exact path='/hedge' component={Hedge} />

        {/* {!isMobile ? (
          <> */}
        <Route exact path='/loads/create-load' component={CreateLoads} />
        <Route exact path='/loads/active-load' component={ActiveLoads} />
        <Route exact path='/load/:loadId' component={LoadsCart} />
        <Route exact path='/loads/load/:loadId' component={LoadsCart} />
        {/* </>
        ) : (
          <>
            <Route exact path="/loads/create-load" component={CreateLoadsV2} />
            <Route exact path="/loads/active-load" component={ActiveLoadsV2} />
            <Route exact path="/load/:loadId" component={LoadsCartV2} />
            <Route exact path="/loads/load/:loadId" component={LoadsCartV2} />
          </>
        )} */}
        <Route exact path='/settlement/open' component={Settlement} />
        <Route
          exact
          path='/settlement/open/create/:settlementId?'
          component={CreateSettlement}
        />
        <Route
          exact
          path='/settlement/completed/create/:settlementId?'
          component={CreateSettlement}
        />
        <Route exact path='/settlement/completed' component={Settlement} />
        <Route exact path='/payment' component={Payment} />
        <Route exact path='/config/payment' component={ConfigPayment} />
        
        {/* Virtual Grading Routes */}
        <Route exact path='/virtual-grading' component={VirtualGrading} />
        <Route exact path='/virtual-grading/upload' component={UploadItem} />
        <Route exact path='/virtual-grading/item/:itemId' component={ItemDetail} />
        <Route exact path='/virtual-grading/admin' component={AdminDashboard} />
        <Route exact path='/virtual-grading/admin/grade/:itemId' component={GradeItem} />
        <Route exact path='/virtual-grading/admin/items' component={AdminItemsList} />
      </Switch>
    </Router>
  );
}

export default App;
